import React, { useState, useEffect } from "react";
import AthleteLayout from "./common/AthleteLayout";
import { AthleteHeader } from "./common/AthleteHeader";
import { useAthleteAuthContext } from "./login/useAthleteAuthContext";
import API_URL from "../api/API";

export const AthleteMessages = () => {
  const { athlete } = useAthleteAuthContext();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  // For replies
  const [replyInputs, setReplyInputs] = useState({});
  const [replyAttachments, setReplyAttachments] = useState({});

  // Fetch all messages for athlete
  const fetchMessages = async () => {
    if (!athlete) return [];
    setLoading(true);
    try {
      const response = await fetch(
        API_URL.FRONTEND_ATHLETE_GET_ALL_MESSAGES + athlete.id,
        { headers: { Authorization: `Bearer ${athlete.token}` } }
      );
      const data = await response.json();

      if (data.success) {
        const messagesArray = Array.isArray(data.messages) ? data.messages : [];
        const sorted = [...messagesArray].sort(
          (b, a) => new Date(a.timestamp) - new Date(b.timestamp)
        );
        setMessages(sorted);

         // Automatically mark unread AMAX messages as read
        sorted.forEach(msg => {
          if (msg.sender_type === "amax" && !msg.read) {
            markAsRead(msg._id);
          }

          // Also mark unread AMAX replies as read
          msg.replies?.forEach(reply => {
            if (reply.sender_type === "amax" && !reply.read) {
              markAsRead(reply._id);
            }
          });
        });

        return sorted;
      } else {
        console.error("Failed to fetch messages:", data.error || data);
        return [];
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
      return [];
    } finally {
      setLoading(false);
    }
  };


  const markAsRead = async () => {
    try {
      await fetch(`${API_URL.FRONTEND_ATHLETE_MARK_MESSAGE_AS_READ}${athlete.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${athlete.token}` },
      });
  
      // Update local state so all messages appear read
      setMessages(prev =>
        prev.map(msg => ({ ...msg, read: true }))
      );
    } catch (err) {
      console.error("Failed to mark messages as read:", err);
    }
  };
  
  

  useEffect(() => {
    if (!athlete) return;
    fetchMessages();
  }, [athlete]);

  // Handle file input for new messages
  const handleFileChange = (e) => {
    setAttachments(Array.from(e.target.files));
  };

  // Handle file input for replies
  const handleReplyFileChange = (msgId, files) => {
    setReplyAttachments(prev => ({ ...prev, [msgId]: Array.from(files) }));
  };

  // Handle reply input change
  const handleReplyChange = (msgId, value) => {
    setReplyInputs(prev => ({ ...prev, [msgId]: value }));
  };

  // Send a new message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && attachments.length === 0) return;

    const formData = new FormData();
    formData.append("sender_id", athlete.id);
    formData.append("message", newMessage);
    formData.append("sender_type", "athlete");
    formData.append("receiver_type", "amax");
    formData.append("receiver_id", "amax");
    attachments.forEach((file) => formData.append("attachments", file));

    setSending(true);
    try {
      const res = await fetch(API_URL.SEND_ATHLETE_MESSAGE_TO_AMAX, {
        method: "POST",
        headers: { Authorization: `Bearer ${athlete.token}` },
        body: formData,
      });
      const data = await res.json();

      if (data.success) {
        setNewMessage("");
        setAttachments([]);
        await fetchMessages();
        setSuccessMsg("Message sent successfully!");
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        console.error("Failed to send message:", data.error || data);
      }
    } catch (err) {
      console.error("Error sending message:", err);
    } finally {
      setSending(false);
    }
  };

  // Send a reply to a staff message
  const handleSendReply = async (parentMessageId) => {
    const text = replyInputs[parentMessageId];
    const files = replyAttachments[parentMessageId] || [];

    if (!text && files.length === 0) return;

    const formData = new FormData();
    formData.append("sender_id", athlete.id);
    formData.append("sender_type", "athlete");
    formData.append("receiver_id", "amax");
    formData.append("receiver_type", "amax");
    formData.append("message", text || "");
    formData.append("message_id", parentMessageId); // ✅ important fix
    files.forEach(file => formData.append("attachments", file));

    try {
      const res = await fetch(API_URL.SEND_ATHLETE_MESSAGE_TO_AMAX, {
        method: "POST",
        headers: { Authorization: `Bearer ${athlete.token}` },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        await fetchMessages();
        setReplyInputs(prev => ({ ...prev, [parentMessageId]: "" }));
        setReplyAttachments(prev => ({ ...prev, [parentMessageId]: [] }));
        setSuccessMsg("Reply sent successfully!");
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (err) {
      console.error("Error sending reply:", err);
    }
  };

  // Delete a message
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this message?")) return;

    setMessages((prevMessages) =>
      prevMessages.filter((msg) => msg._id?.toString() !== id.toString())
    );

    try {
      const res = await fetch(`${API_URL.FRONTEND_ATHLETE_DELETE_MESSAGE}${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${athlete.token}` },
      });

      const data = await res.json();

      if (!data.success) {
        console.error("Failed to delete message:", data.error || data);
        fetchMessages();
      }
    } catch (err) {
      console.error("Error deleting message:", err);
      fetchMessages();
    }
  };

  // Returns background color based on sender type
const getMessageBg = (senderType) => {
  if (senderType === "athlete") return "bg-blue-50"; // Athlete messages/replies
  if (senderType === "amax") return "bg-green-50";   // AMAX messages/replies
  return "bg-gray-50"; // fallback
};


  return (
    <div className="bg-white min-h-screen">
      <AthleteLayout>
      <div className="h-full flex flex-col bg-white">
        <div className="flex-1 overflow-y-auto">
          <div className="w-full max-w-2xl mx-auto px-4 py-6">

            <h2 className="text-2xl font-bold mb-5 text-black">Messages</h2>

            {successMsg && (
              <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-800 rounded">
                {successMsg}
              </div>
            )}

            {/* New Message Composer */}
            <form
              onSubmit={handleSendMessage}
              className="border border-gray-200 rounded-lg p-4 mb-6 bg-smock shadow-sm"
            >
              <textarea
                className="w-full border border-gray-300 rounded-lg p-3 text-base font-light focus:outline-none focus:ring-2 focus:ring-darkYellow"
                placeholder="Type your message to AMAX..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                rows={3}
              />
              <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
                <input type="file" multiple onChange={handleFileChange} className="text-sm" />
                <button
                  type="submit"
                  disabled={sending}
                  className="text-center px-6 xs:px-8 py-3 xs:py-4 text-white text-base xs:text-xl leading-none bg-black hover:bg-black/70 rounded-2xl transition-all duration-300"
                >
                  {sending ? "Sending..." : "Send"}
                </button>
              </div>
            </form>

            {/* Messages Section */}
            {loading ? (
              <p className="text-gray-500">Loading messages...</p>
            ) : messages.length === 0 ? (
              <p className="text-gray-500">No messages found.</p>
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">

                {messages.map((msg) => (
                <div key={msg._id} className={`p-3 sm:p-4 rounded-lg mb-2 ${getMessageBg(msg.sender_type)}`}>

                    {/* Message header */}
                    <div className="flex justify-between items-center">
                      <p className="text-sm font-medium text-gray-800">
                        {msg.sender_type === "athlete" ? "You" : "AMAX"}
                      </p>
                      <p className="text-xs text-gray-400">{new Date(msg.timestamp).toLocaleString()}</p>
                    </div>

                    <p className="text-gray-700 mt-1">{msg.message}</p>

                    {msg.attachments?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {msg.attachments.map((file, idx) => (
                          <a
                            key={idx}
                            href={`${API_URL.SERVER_PATH.replace(/\/$/, '')}${file}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-blue-600 text-sm hover:underline"
                          >
                            Attachment {idx + 1}
                          </a>
                        ))}
                      </div>
                    )}

                    {/* Replies */}
                    {msg.replies?.length > 0 && (
                      <div className="mt-3 pl-4 border-l-2 border-gray-300">
                        {msg.replies.map((reply) => (
                        <div key={reply._id} className={`mb-2 p-2 rounded ${getMessageBg(reply.sender_type)}`}>

                            <div className="flex justify-between items-center">
                              <p className="text-sm font-medium text-gray-800">
                                {reply.sender_type === "athlete" ? "You" : "AMAX"}
                              </p>
                              <p className="text-xs text-gray-400">{new Date(reply.timestamp).toLocaleString()}</p>
                            </div>
                            <p className="text-gray-700 mt-1">{reply.message}</p>
                            {reply.attachments?.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {reply.attachments.map((file, idx) => (
                                  <a
                                    key={idx}
                                    href={`${API_URL.SERVER_PATH.replace(/\/$/, '')}${file}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block text-blue-600 text-sm hover:underline"
                                  >
                                    Attachment {idx + 1}
                                  </a>
                                ))}
                         
                              </div>
                            )}

                                {/* Delete button for replies */}
                                {reply.sender_type === "athlete" && (
                                  <button
                                    onClick={() => handleDelete(reply._id)}
                                    className="mt-1 text-red-600 text-xs hover:underline"
                                  >
                                    Delete
                                  </button>
                                )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Reply box (only for staff messages) */}
                    {msg.sender_type === "amax" && (
                      <div className="mt-3 pl-4 border-l-2 border-gray-300">
                        <textarea
                          className="w-full border p-2 rounded mt-2 text-sm resize-none" 
                          rows={2}
                          placeholder="Write a reply..."
                          value={replyInputs[msg._id] || ""}
                          onChange={(e) => handleReplyChange(msg._id, e.target.value)}
                        />
                        <input
                          type="file"
                          multiple
                          onChange={(e) => handleReplyFileChange(msg._id, e.target.files)}
                          className="mt-1 text-sm"
                        />
                        <button
                          className="mt-2 px-4 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                          onClick={() => handleSendReply(msg._id)}
                        >
                          Send Reply
                        </button>
                      </div>
                    )}

                    {/* Delete button for athlete messages */}
                    {msg.sender_type === "athlete" && (
                      <button
                        onClick={() => handleDelete(msg._id)}
                        className="mt-2 text-red-600 text-xs hover:underline"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        </div>
      </AthleteLayout>
    </div>
  );
};
